// ПвП.6.1: engine-shim — drop-in замена для engine-bridge в BattleScreen.
//
// Идея: BattleScreen импортирует не из ../engine-bridge напрямую, а отсюда.
// Большинство функций — pass-through (read-only валидация, шаблоны и т.п.).
// Action-мутирующие функции (setPendingPlacement, heroAttackOrSkip, etc.)
// проверяют match._pvp — и если true, шлют в room.send вместо локального
// движка. Так BattleScreen остаётся почти без хирургии.
//
// match._pvp = true ставится в match-adapter.colyseusToMatch.

/* eslint-disable @typescript-eslint/no-explicit-any */
import * as eb from '../engine-bridge';
import { getCurrentRoom } from './client';

// ─── Константы и pass-through ────────────────────────────────────
export const PHASE                       = eb.PHASE;
export const INFLUENCE_PER_CARD_LIMIT    = eb.INFLUENCE_PER_CARD_LIMIT;
export const GLOBAL_PRIORITIES           = eb.GLOBAL_PRIORITIES;

export const createMatchForBattle        = eb.createMatchForBattle;
export const applyMulligan               = eb.applyMulligan;
export const finishMulligan              = eb.finishMulligan;

// Boot-API серверной комнаты (для прямого использования из MatchmakingScreen).
// Здесь не используется, но удобно держать.

// ─── Read-only валидации ─────────────────────────────────────────
// В PvP — сервер всё валидирует сам. Возвращаем оптимистичное {ok:true}.
// Это упростит UX (кнопка «положить» не серая «на всякий случай»), а если
// сервер отклонит — придёт onStateChange без изменений, как будто игрок
// промахнулся. Можно потом добавить toast «отказ сервера».
export const canAddPlacement = (match: any, idx: 0 | 1, card: any) => {
  if (match?._pvp) return { ok: true };
  return eb.canAddPlacement(match, idx, card);
};
export const canPlayInfluenceCard = (match: any, idx: 0 | 1, cardId: string) => {
  if (match?._pvp) return { ok: true };
  return eb.canPlayInfluenceCard(match, idx, cardId);
};

// Полностью read-only — pass-through всегда.
export const canHeroAttackHero           = eb.canHeroAttackHero;
export const canHeroAttackUnit           = eb.canHeroAttackUnit;
export const actionInitiationTurn        = eb.actionInitiationTurn;
export const actionNeedsTarget           = eb.actionNeedsTarget;
export const getActionTargets            = eb.getActionTargets;
export const needsInfluenceTarget        = eb.needsInfluenceTarget;
export const getInfluenceTargets         = eb.getInfluenceTargets;
export const getInfluenceCardTemplates   = eb.getInfluenceCardTemplates;
export const getInfluencePlayCount       = eb.getInfluencePlayCount;

// getHeroActiveInfo читает match.players[idx]; в PvP-адаптере есть hero.heroActiveUsedThisMatch,
// engine читает оба флага. Полагаемся на pass-through.
export const getHeroActiveInfo           = eb.getHeroActiveInfo;

// Bot-функции — в PvP не вызываются (BattleScreen short-circuit'ит ботов).
export const botMulligan                 = eb.botMulligan;
export const botPlacement                = eb.botPlacement;
export const applyBotPlacementDecision   = eb.applyBotPlacementDecision;
export const botModPlay                  = eb.botModPlay;
export const botInfluencePlay            = eb.botInfluencePlay;
export const botHeroAttack               = eb.botHeroAttack;

// Стелс — getActivatableStealth/getStealthTargets pass-through (read-only),
// activateStealthUnit роутится в room.send в PvP (ПвП.6.4).
export const getActivatableStealth       = eb.getActivatableStealth;
export const getStealthTargets           = eb.getStealthTargets;

// Приказы / Приоритет атаки — pass-through.
export const determinePlacementPrompt    = eb.determinePlacementPrompt;
// 2d/PvP: выбор цели Приказа при выкладке (Фиксер/Хирург Vita/Сапёр/Био Мор).
// В PvP setPendingOrderOpts складывает opts в stash, а setPendingPlacement
// сериализует их в payload (unit-объекты → instanceId через суффикс '__id').
// Так сервер (authoritative) узнаёт выбор игрока, а не применяет дефолт.
const pvpOrderOptsStash = new Map<number, any>();
export function setPendingOrderOpts(match: any, idx: 0 | 1, cardInstanceId: number, opts: any) {
  if (match?._pvp) {
    pvpOrderOptsStash.set(cardInstanceId, opts);
    return;
  }
  return eb.setPendingOrderOpts(match, idx, cardInstanceId, opts);
}
export const needsAttackPriorityPicker   = eb.needsAttackPriorityPicker;
export const setUnitAttackPriority       = eb.setUnitAttackPriority;
// 2d reveal-pick: peek Модов из колоды (PvE — локальный движок; в PvP клиент
// колоду не видит, там вскрытие идёт через reveal_request на сервер).
export const revealModsFromDeck          = eb.revealModsFromDeck;
// 2d: приоритет ЛЕЧЕНИЯ для хилеров — pass-through.
export const HEAL_PRIORITIES             = eb.HEAL_PRIORITIES;
export const needsHealPriorityPicker     = eb.needsHealPriorityPicker;
export const setUnitHealPriority         = eb.setUnitHealPriority;

// ─── Action-мутаторы: роутятся в room.send в PvP ─────────────────

export function setPendingPlacement(match: any, idx: 0 | 1, payload: any) {
  if (match?._pvp) {
    // payload = { cardInstanceId, line, slot } для юнитов,
    //         или { cardInstanceId, targetInstanceId } для Модов.
    // ПвП.fix-priority: setUnitAttackPriority вызывается ДО setPendingPlacement
    // и мутирует card.attackPriority в локальной hand. Достаём его и шлём
    // на сервер (иначе сервер не узнает выбор игрока и юнит станет с
    // дефолтным 'random' для Глобала).
    const card = match.players?.[idx]?.hand?.find(
      (c: any) => c.instanceId === payload.cardInstanceId
    );
    // 2d: healPriority прокидываем так же, как attackPriority — иначе сервер
    // не узнает выбор игрока и хилер будет лечить по дефолту lowest_hp.
    const enrichedPayload: any = { ...payload };
    if (card?.attackPriority) enrichedPayload.attackPriority = card.attackPriority;
    if (card?.healPriority) enrichedPayload.healPriority = card.healPriority;
    // 2d/PvP: order-opts (выбор цели Приказа). Сериализуем unit-объекты в
    // instanceId (ключ + '__id'); примитивы (sourceInstanceId/line/slot/...) — как есть.
    const stashed = pvpOrderOptsStash.get(payload.cardInstanceId);
    if (stashed) {
      const ser: any = {};
      for (const [k, v] of Object.entries(stashed)) {
        if (v && typeof v === 'object' && (v as any).instanceId != null) ser[`${k}__id`] = (v as any).instanceId;
        else ser[k] = v;
      }
      enrichedPayload.orderOpts = ser;
      pvpOrderOptsStash.delete(payload.cardInstanceId);
    }
    getCurrentRoom()?.send('placement', enrichedPayload);
    return;
  }
  return eb.setPendingPlacement(match, idx, payload);
}

export function cancelPendingPlacement(match: any, idx: 0 | 1, cardInstanceId: number) {
  if (match?._pvp) {
    getCurrentRoom()?.send('cancel_placement', { cardInstanceId });
    return;
  }
  return eb.cancelPendingPlacement(match, idx, cardInstanceId);
}

export function setPlayerPendingAction(
  match: any, idx: 0 | 1, cardInstanceId: number,
  targetInstanceId: number | null, _opts: any
) {
  if (match?._pvp) {
    // ПвП.fix-move-action: opts (destLine/destSlot для Перегруппировки и
    // Передислокации) теперь идут на сервер, иначе resolveAction вернёт
    // {ok:false, reason:'no_destination'} и Действие просто сгорит.
    const payload: any = { cardInstanceId, targetInstanceId };
    if (_opts?.destLine != null) payload.destLine = _opts.destLine;
    if (_opts?.destSlot != null) payload.destSlot = _opts.destSlot;
    // 2d: мультиселект (Великая жертва — выбранные юниты-жертвы).
    if (Array.isArray(_opts?.selectedInstanceIds)) payload.selectedInstanceIds = _opts.selectedInstanceIds;
    // 2d reveal-pick (Дозаправка/Конструктор): выбранные Моды. Вскрытый набор
    // сервер хранит у себя (pendingReveal) — клиент шлёт только выбор.
    if (Array.isArray(_opts?.pickedInstanceIds)) payload.pickedInstanceIds = _opts.pickedInstanceIds;
    getCurrentRoom()?.send('play_action', payload);
    return;
  }
  return eb.setPlayerPendingAction(match, idx, cardInstanceId, targetInstanceId, _opts);
}

export function clearPlayerPendingAction(match: any, idx: 0 | 1) {
  if (match?._pvp) {
    // На сервере нет отдельного «снять pending action» — клиент в PvP
    // эту операцию не делает (UI не показывает уже-pending карту с кнопкой
    // отмены, потому что pendingAction не синкается). No-op безопасен.
    return;
  }
  return eb.clearPlayerPendingAction(match, idx);
}

// «Закончить ход» — в PvP это просто `end_turn`. Анимация Ф3 у нас пока
// не реализована для PvP — сервер прокручивает фазы и шлёт обновления.
export async function commitPlacementsAndReveal(match: any, anim: any): Promise<void> {
  if (match?._pvp) {
    getCurrentRoom()?.send('end_turn');
    return; // не дожидаемся — state придёт через onStateChange
  }
  return eb.commitPlacementsAndReveal(match, anim);
}

export function heroAttackOrSkip(match: any, target: any) {
  if (match?._pvp) {
    pvpSendHeroAttack(target);
    return;
  }
  return eb.heroAttackOrSkip(match, target);
}

// БОЙ.1.9: async-версия. В PvP — шлём hero_attack (анимация придёт от сервера
// через combatEvents в ПвП.8). В PvE — eb.heroAttackOrSkipAnimated с anim.
export async function heroAttackOrSkipAnimated(match: any, target: any, anim?: any): Promise<void> {
  if (match?._pvp) {
    pvpSendHeroAttack(target);
    return;
  }
  return eb.heroAttackOrSkipAnimated(match, target, anim);
}

export function performHeroAttackKeepPhase(match: any, target: any) {
  if (match?._pvp) {
    pvpSendHeroAttack(target);
    return true;
  }
  return eb.performHeroAttackKeepPhase(match, target);
}

export async function performHeroAttackKeepPhaseAnimated(match: any, target: any, anim?: any): Promise<boolean> {
  if (match?._pvp) {
    pvpSendHeroAttack(target);
    return true;
  }
  return eb.performHeroAttackKeepPhaseAnimated(match, target, anim);
}

function pvpSendHeroAttack(target: any) {
  const room = getCurrentRoom();
  if (!room) return;
  if (target == null) {
    room.send('hero_attack', { target: null });
  } else if (target.type === 'hero' || (target.faction !== undefined && target.atk !== undefined && !target.instanceId)) {
    // Hero card (атака по Герою врага).
    room.send('hero_attack', { target: 'hero' });
  } else if (typeof target.instanceId === 'number') {
    room.send('hero_attack', { target: target.instanceId });
  }
}

export function playInfluenceCard(
  match: any, idx: 0 | 1, cardId: string, targetCard: any = null
): { ok: boolean; reason?: string } {
  if (match?._pvp) {
    const room = getCurrentRoom();
    if (!room) return { ok: false, reason: 'no room' };
    const targetInstanceId = targetCard?.instanceId ?? null;
    room.send('play_influence', { cardId, targetInstanceId });
    return { ok: true };
  }
  return eb.playInfluenceCard(match, idx, cardId, targetCard);
}

// АНИМ.11: async-вариант с anim.influence + diff-snapshot. PvE-путь.
// В PvP сервер сам анимирует, тут только room.send.
export async function playInfluenceCardWithAnim(
  match: any, idx: 0 | 1, cardId: string, targetCard: any, animator: any
): Promise<{ ok: boolean; reason?: string }> {
  if (match?._pvp) {
    return playInfluenceCard(match, idx, cardId, targetCard);
  }
  return eb.playInfluenceCardWithAnim(match, idx, cardId, targetCard, animator);
}

// ПвП.6.4: активация Стелс-юнита в Ф4/Ф5. В PvP шлём server-handler.
export async function activateStealthUnit(
  match: any, attackerIdx: 0 | 1, attacker: any, target: any
): Promise<any> {
  if (match?._pvp) {
    const room = getCurrentRoom();
    if (!room) return null;
    let serverTarget: 'hero' | number | null = null;
    if (target && (target.type === 'hero' || (target.faction !== undefined && !target.instanceId))) {
      serverTarget = 'hero';
    } else if (target?.instanceId) {
      serverTarget = target.instanceId;
    }
    room.send('activate_stealth', {
      attackerInstanceId: attacker?.instanceId,
      target: serverTarget,
    });
    return null; // server применит и пришлёт новый state через onStateChange
  }
  return eb.activateStealthUnit(match, attackerIdx, attacker, target);
}

export function useHeroActive(
  match: any, idx: 0 | 1, target?: any
): { ok: boolean; reason?: string } {
  if (match?._pvp) {
    const room = getCurrentRoom();
    if (!room) return { ok: false, reason: 'no room' };
    // Адаптируем target под HeroActiveSchema на сервере.
    let payload: any = null;
    if (target != null) {
      if (target.card && target.line != null && target.slot != null) {
        // Кироса: { card, line, slot }
        payload = {
          instanceId: target.card.instanceId,
          line: target.line, slot: target.slot,
        };
      } else if (target.source && target.dest) {
        // Раст: { source, dest } — оба свои.
        payload = {
          sourceInstanceId: target.source.instanceId,
          destInstanceId: target.dest.instanceId,
        };
      } else if (target.instanceId != null && target.ownerIdx != null) {
        // Лиза-сброс: { instanceId, ownerIdx }
        payload = { instanceId: target.instanceId, ownerIdx: target.ownerIdx };
      } else if (target.instanceId != null) {
        // Юнит на поле.
        payload = { instanceId: target.instanceId };
      }
    }
    room.send('hero_active', { target: payload });
    return { ok: true };
  }
  return eb.useHeroActive(match, idx, target);
}

// АНИМ.10: async-вариант с diff-snapshot для анимаций heal/buff/summon/
// damage. В PvP-режиме идёт через тот же room.send (сервер сам делает
// runWithAnim + runSyncWithDiff). В PvE-режиме — eb.useHeroActiveWithAnim
// с локальным animator'ом.
export async function useHeroActiveWithAnim(
  match: any, idx: 0 | 1, target: any, animator: any
): Promise<{ ok: boolean; reason?: string }> {
  if (match?._pvp) {
    return useHeroActive(match, idx, target);
  }
  return eb.useHeroActiveWithAnim(match, idx, target, animator);
}

// ─── ВОЙ.1: Война Корпораций (snap-механика) ─────────────────────
// Только в PvP-режиме (PvE войны нет). В обоих случаях стейт хранится
// на сервере в MatchState — клиент читает match.war*, отправляет
// действия через room.send.

export function declareWarSnap(match: any): void {
  if (!match?._pvp) return;
  const room = getCurrentRoom();
  if (!room) return;
  room.send('snap_declare', {});
}

export function respondWarSnap(match: any, accept: boolean): void {
  if (!match?._pvp) return;
  const room = getCurrentRoom();
  if (!room) return;
  room.send('snap_respond', { accept });
}

// ─── Types pass-through ──────────────────────────────────────────
export type Match              = eb.Match;
export type InfluenceCardTpl   = eb.InfluenceCardTpl;
export type GlobalPriorityDef  = eb.GlobalPriorityDef;
export type HealPriorityDef    = eb.HealPriorityDef;
